import React, { useEffect, useState } from "react";
import { Save, ExternalLink } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";

const empty = {
  eyebrow: "", heading: "", intro: "",
  price_hourly: "", price_half_day: "", price_half_day_note: "", price_full_day: "", price_full_day_note: "", discount_note: "",
  included: "", capacity: "", perfect_for: "", location: "",
  breakout_body: "", recording: "",
  image_1: "", image_2: "", image_3: "",
  cta_body: "", cta_email: "", cta_phone: "",
};

export default function MeetingRoomManager() {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.meetingRoom.get().then((d) => setForm({ ...empty, ...d })).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await adminApi.meetingRoom.update(form); toast.success("Meeting room page saved"); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-extrabold text-2xl text-navy">Meeting Room Hire Page</h1>
        <a href="/meeting-room-hire" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange"><ExternalLink size={14} /> View page</a>
      </div>
      <p className="text-slate-500 text-sm mb-8">Edit the copy and upload photos of your meeting room. Empty photo slots simply won't appear on the page.</p>

      <div className="max-w-2xl space-y-8">
        <Card title="Header & intro">
          <Field label="Eyebrow (small label above heading)"><input data-testid="mr-eyebrow" value={form.eyebrow} onChange={(e) => set("eyebrow", e.target.value)} className={inputCls} /></Field>
          <Field label="Heading"><input data-testid="mr-heading" value={form.heading} onChange={(e) => set("heading", e.target.value)} className={inputCls} /></Field>
          <Field label="Intro text" hint="Separate paragraphs with a blank line. The first paragraph shows as the hero tagline."><textarea data-testid="mr-intro" rows={6} value={form.intro} onChange={(e) => set("intro", e.target.value)} className={inputCls} /></Field>
        </Card>

        <Card title="Photos (3 pictures)">
          <p className="text-sm text-slate-500 -mt-2">Upload up to three photos of the meeting room. They appear as a gallery near the top of the page. Empty slots are simply skipped.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <ImageUpload label="Picture 1" value={form.image_1} onChange={(v) => set("image_1", v)} />
            <ImageUpload label="Picture 2" value={form.image_2} onChange={(v) => set("image_2", v)} />
            <ImageUpload label="Picture 3" value={form.image_3} onChange={(v) => set("image_3", v)} />
          </div>
        </Card>

        <Card title="Prices">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Hourly price"><input data-testid="mr-price-hourly" value={form.price_hourly} onChange={(e) => set("price_hourly", e.target.value)} className={inputCls} placeholder="£10 per hour" /></Field>
            <div />
            <Field label="Half day price"><input data-testid="mr-price-half-day" value={form.price_half_day} onChange={(e) => set("price_half_day", e.target.value)} className={inputCls} placeholder="£30 Half Day" /></Field>
            <Field label="Half day note"><input data-testid="mr-price-half-day-note" value={form.price_half_day_note} onChange={(e) => set("price_half_day_note", e.target.value)} className={inputCls} placeholder="9am – 1pm or 1.30pm – 5.30pm" /></Field>
            <Field label="Full day price"><input data-testid="mr-price-full-day" value={form.price_full_day} onChange={(e) => set("price_full_day", e.target.value)} className={inputCls} placeholder="£50 Full Day" /></Field>
            <Field label="Full day note"><input data-testid="mr-price-full-day-note" value={form.price_full_day_note} onChange={(e) => set("price_full_day_note", e.target.value)} className={inputCls} placeholder="9am – 5.30pm" /></Field>
          </div>
          <Field label="Discount note"><input data-testid="mr-discount" value={form.discount_note} onChange={(e) => set("discount_note", e.target.value)} className={inputCls} /></Field>
        </Card>

        <Card title="What's Included & Room Capacity">
          <Field label="What's included" hint="One item per line."><textarea data-testid="mr-included" rows={6} value={form.included} onChange={(e) => set("included", e.target.value)} className={inputCls} /></Field>
          <Field label="Room capacity" hint="One item per line."><textarea data-testid="mr-capacity" rows={4} value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className={inputCls} /></Field>
        </Card>

        <Card title="Perfect For">
          <Field label="Perfect for" hint="One item per line."><textarea data-testid="mr-perfect-for" rows={7} value={form.perfect_for} onChange={(e) => set("perfect_for", e.target.value)} className={inputCls} /></Field>
        </Card>

        <Card title="Convenient Location">
          <Field label="Location benefits" hint="One item per line."><textarea data-testid="mr-location" rows={3} value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} /></Field>
        </Card>

        <Card title="Breakout Area & Content Recording">
          <Field label="Breakout area text"><textarea data-testid="mr-breakout" rows={3} value={form.breakout_body} onChange={(e) => set("breakout_body", e.target.value)} className={inputCls} /></Field>
          <Field label="Content recording uses" hint="One item per line."><textarea data-testid="mr-recording" rows={4} value={form.recording} onChange={(e) => set("recording", e.target.value)} className={inputCls} /></Field>
        </Card>

        <Card title="Booking / contact">
          <Field label="Booking text"><textarea data-testid="mr-cta-body" rows={3} value={form.cta_body} onChange={(e) => set("cta_body", e.target.value)} className={inputCls} /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Contact email"><input value={form.cta_email} onChange={(e) => set("cta_email", e.target.value)} className={inputCls} /></Field>
            <Field label="Contact phone"><input value={form.cta_phone} onChange={(e) => set("cta_phone", e.target.value)} className={inputCls} /></Field>
          </div>
        </Card>

        <button data-testid="mr-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-full transition-colors">
          <Save size={16} /> {saving ? "Saving..." : "Save page"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";
const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-semibold text-navy mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);
const Card = ({ title, children }) => (
  <div className="space-y-5 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
    <h2 className="font-display font-bold text-navy border-b border-slate-100 pb-3">{title}</h2>
    {children}
  </div>
);
